import { cva } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '@/utils';

// Context to manage menu closing
export interface MenuContextValue {
  closeMenu?: () => void;
}

export const MenuContext = React.createContext<MenuContextValue>({});

export const useMenuContext = (): MenuContextValue => {
  return React.useContext(MenuContext);
};

const menuVariants = cva(
  'bg-magical-moonlight-white border border-magical-moonlight-d1 rounded-2xl shadow-lg overflow-hidden',
  {
    variants: {
      size: {
        sm: 'min-w-[160px]',
        md: 'min-w-[200px]',
        lg: 'min-w-[240px]',
      },
    },
    defaultVariants: {
      size: 'md',
    },
  }
);

const menuItemVariants = cva(
  'flex items-center w-full px-4 py-3 text-sm font-medium text-left transition-all duration-200',
  {
    variants: {
      variant: {
        default:
          'text-magical-moonlight-d3 hover:bg-magical-moonlight-l hover:text-magical-moonlight-d4',
        destructive:
          'text-fusion-red hover:bg-fusion-red/10 hover:text-fusion-red',
      },
      disabled: {
        true: 'text-magical-moonlight-d1 cursor-not-allowed opacity-50',
        false: 'cursor-pointer',
      },
    },
    defaultVariants: {
      variant: 'default',
      disabled: false,
    },
  }
);

export interface MenuProps extends React.HTMLAttributes<HTMLDivElement> {
  size?: 'sm' | 'md' | 'lg';
}

export interface MenuItemProps extends React.HTMLAttributes<HTMLButtonElement> {
  variant?: 'default' | 'destructive';
  disabled?: boolean;
  icon?: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  children: React.ReactNode;
}

export type MenuSeparatorProps = React.HTMLAttributes<HTMLDivElement>;

export interface MenuTriggerProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
  menu: React.ReactNode;
  align?: 'start' | 'end' | 'center';
  side?: 'top' | 'bottom';
  onOpenChange?: (open: boolean) => void;
  isOpen?: boolean;
  defaultIsOpen?: boolean;
  menuClassName?: string;
}

const Menu = React.forwardRef<HTMLDivElement, MenuProps>(
  ({ className, size, children, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(menuVariants({ size }), className)}
        role="menu"
        {...props}
      >
        {children}
      </div>
    );
  }
);

const MenuItem = React.forwardRef<HTMLButtonElement, MenuItemProps>(
  ({ className, variant, disabled, icon: Icon, children, ...props }, ref) => {
    return (
      <button
        ref={ref}
        className={cn(menuItemVariants({ variant, disabled }), className)}
        disabled={disabled}
        role="menuitem"
        {...props}
      >
        {Icon ? <Icon className="mr-2 size-4" /> : null}
        {children}
      </button>
    );
  }
);

const MenuSeparator = React.forwardRef<HTMLDivElement, MenuSeparatorProps>(
  ({ className, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn('border-t border-magical-moonlight-d1', className)}
        role="separator"
        {...props}
      />
    );
  }
);

const MenuTrigger = React.forwardRef<HTMLDivElement, MenuTriggerProps>(
  (
    {
      className,
      children,
      menu,
      align = 'start',
      side = 'bottom',
      onOpenChange,
      isOpen: controlledIsOpen,
      defaultIsOpen = false,
      menuClassName,
      ...props
    },
    ref
  ) => {
    const [uncontrolledIsOpen, setUncontrolledIsOpen] =
      React.useState(defaultIsOpen);
    const triggerRef = React.useRef<HTMLDivElement>(null);
    const menuRef = React.useRef<HTMLDivElement>(null);

    const isControlled = controlledIsOpen !== undefined;
    const isOpen = isControlled ? controlledIsOpen : uncontrolledIsOpen;

    // Update open state and notify parent
    const updateOpenState = React.useCallback(
      (open: boolean) => {
        if (!isControlled) {
          setUncontrolledIsOpen(open);
        }
        onOpenChange?.(open);
      },
      [isControlled, onOpenChange]
    );

    // Handle click outside to close menu
    // eslint-disable-next-line consistent-return
    React.useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        // Check if any modal is currently open by looking for modal elements
        const hasOpenModal = document.querySelector('[role="dialog"]') !== null;
        if (
          triggerRef.current &&
          menuRef.current &&
          !triggerRef.current.contains(event.target as Node) &&
          !menuRef.current.contains(event.target as Node) &&
          !hasOpenModal
        ) {
          updateOpenState(false);
        }
      };

      if (isOpen) {
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
          document.removeEventListener('mousedown', handleClickOutside);
        };
      }
    }, [isOpen, updateOpenState]);

    // Handle escape key
    // eslint-disable-next-line consistent-return
    React.useEffect(() => {
      const handleEscape = (event: KeyboardEvent) => {
        if (event.key === 'Escape') {
          updateOpenState(false);
        }
      };

      if (isOpen) {
        document.addEventListener('keydown', handleEscape);
        return () => {
          document.removeEventListener('keydown', handleEscape);
        };
      }
    }, [isOpen, updateOpenState]);

    const getPositionClasses = () => {
      const base = 'absolute z-50';
      const sideClasses = side === 'top' ? 'bottom-full mb-1' : 'top-full mt-1';
      let alignClasses = 'left-0';
      if (align === 'end') {
        alignClasses = 'right-0';
      } else if (align === 'center') {
        alignClasses = 'left-1/2 -translate-x-1/2';
      }

      return `${base} ${sideClasses} ${alignClasses}`;
    };

    const handleKeyDown = (event: React.KeyboardEvent) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        updateOpenState(!isOpen);
      }
    };

    return (
      <div
        ref={ref}
        className={cn('relative inline-block', className)}
        {...props}
      >
        <div
          ref={triggerRef}
          onClick={() => updateOpenState(!isOpen)}
          onKeyDown={handleKeyDown}
          className="cursor-pointer"
          role="button"
          tabIndex={0}
          aria-expanded={isOpen}
          aria-haspopup="menu"
        >
          {children}
        </div>
        {isOpen ? (
          <div
            ref={menuRef}
            className={cn(getPositionClasses(), menuClassName)}
          >
            <MenuContext.Provider
              value={{ closeMenu: () => updateOpenState(false) }}
            >
              {menu}
            </MenuContext.Provider>
          </div>
        ) : null}
      </div>
    );
  }
);

Menu.displayName = 'Menu';
MenuItem.displayName = 'MenuItem';
MenuSeparator.displayName = 'MenuSeparator';
MenuTrigger.displayName = 'MenuTrigger';

export { Menu, MenuItem, MenuSeparator, MenuTrigger };
